import { ClassType, keysOf, lazy, Lazy, Predicate, registerExtensions, throwError } from '@nx-data/utilities';
import { getJoinMetadata, JoinDecoratorMetadata } from './decorator';
import { BulkDbEvent, DbEventHooks, DbEvents } from './events';
import { CreateModel, Entity, ManyOf, MappedEntityModel, OneOf, PatchModel, ReadOnlyManyOf, ReadOnlyOneOf } from './model';
import { NxDatabase } from './nx-database';

registerExtensions();

/* eslint-disable prefer-arrow/prefer-arrow-functions */
function isOneOfConnection(item: any): item is OneOf<any> {
  return item instanceof OneOf;
}

function isManyOfConnection(item: any): item is ManyOf<any> {
  return item instanceof ManyOf;
}

class LazyConnectionMap<E extends Entity> {
  private readonly innerMap: Map<ClassType<any>, Map<string, Map<string, Lazy<any> | null>>> = new Map();
  private readonly lazyByClassType: Map<ClassType<any>, Lazy<any>[]> = new Map();

  constructor(private readonly joinMetadata: JoinDecoratorMetadata<Entity, E>[]) {
    joinMetadata
      .uniqueBy((it) => it.joinedEntity)
      .forEach((metadata) => {
        this.innerMap.set(metadata.joinedEntity, new Map());
        this.lazyByClassType.set(metadata.joinedEntity, []);
      });
  }

  public getLazy<T extends Entity, TResult extends ReadOnlyOneOf<T> | ReadOnlyManyOf<T>>(
    entityKey: string,
    metadataProperty: string,
    otherClassType: ClassType<T>,
    factory: () => TResult
  ): Lazy<TResult> {
    const classTypeMap = this.innerMap.get(otherClassType) ?? throwError('Unable to get inner map');
    const entityMap = classTypeMap.get(entityKey) ?? throwError(`Entity does not exist with key [${entityKey}]`);

    const existingLazy = entityMap.get(metadataProperty);

    if (existingLazy) {
      return existingLazy;
    }

    const lazyValue = lazy(factory);
    entityMap.set(entityKey, lazyValue);
    this.lazyByClassType.set(otherClassType, [...(this.lazyByClassType.get(otherClassType) ?? []), lazyValue]);
    return lazyValue;
  }

  public entityDeleted(entityKey: string) {
    for (const [, value] of this.innerMap) {
      value.delete(entityKey);
    }
  }

  public entityAdded(entityKey: string) {
    for (const [, value] of this.innerMap) {
      const metaMap: Map<string, Lazy<any> | null> = new Map();
      this.joinMetadata.forEach((it) => metaMap.set(it.propertyKey, null));
      value.set(entityKey, metaMap);
    }
  }

  public entityUpdated(entityKey: string) {
    for (const [, value] of this.innerMap) {
      const nestedMap = value.get(entityKey) ?? throwError(`No nested map exists for entity [${entityKey}]`);
      for (const [, lazyValue] of nestedMap) {
        lazyValue?.reset();
      }
    }
  }

  public classTypeUpdated<T extends Entity>(classType: ClassType<T>) {
    (this.lazyByClassType.get(classType) ?? []).forEach((_lazy) => _lazy.reset());
  }

  public hasDependencyOnClassType<T extends Entity>(classType: ClassType<T>): boolean {
    return this.lazyByClassType.has(classType);
  }
}

export class NxTable<T extends Entity> implements DbEventHooks {
  private readonly repository: Record<string, T> = {};
  private readonly joinMetadata = getJoinMetadata(this.classType);
  private readonly lazyConnectionMap = new LazyConnectionMap(this.joinMetadata);

  constructor(public readonly tableName: string, protected readonly classType: ClassType<T>, private readonly database: NxDatabase) {
    this.database.registerTable(classType, this);
    this.database.eventBus.registerEventListener(this); // Disposed via DB on the event bus
  }

  public onEntitiesAdded<K>(payload: DbEvents['entitiesAdded']): void {
    this.lazyConnectionMap.classTypeUpdated(payload.classType);
    this.signalUpdateIfNecessaryOnDbEvent(payload);
  }

  public onEntitiesRemoved<K>(payload: DbEvents['entitiesRemoved']): void {
    this.lazyConnectionMap.classTypeUpdated(payload.classType);
    this.signalUpdateIfNecessaryOnDbEvent(payload);
  }

  public onEntitiesUpdated<K>(payload: DbEvents['entitiesUpdated']): void {
    this.lazyConnectionMap.classTypeUpdated(payload.classType);
    this.signalUpdateIfNecessaryOnDbEvent(payload);
  }

  public has(id: string): boolean {
    return id in this.repository;
  }

  public hasAny(): boolean {
    return keysOf(this.repository).anyItems();
  }

  public get(id: string): MappedEntityModel<T> {
    return this.mapEntityToMappedModel(this._get(id));
  }

  public select(predicate: Predicate<T>): MappedEntityModel<T>[] {
    return (Object.values(this.repository) as T[]).filter(predicate).map((it) => this.mapEntityToMappedModel(it));
  }

  public insert(item: CreateModel<T>, dispatchUpdate = true): MappedEntityModel<T> {
    return this.mapEntityToMappedModel(this.insertInternal(item, dispatchUpdate));
  }

  public insertMultiple(
    items: CreateModel<T>[],
    onConflict: 'error' | 'skip' | 'update' = 'update',
    dispatchEvent = true
  ): MappedEntityModel<T>[] {
    return this.insertMultipleInternal(items, onConflict, dispatchEvent).map((it) => this.mapEntityToMappedModel(it));
  }

  public update(id: string, item: PatchModel<T>, dispatchEvent = true): MappedEntityModel<T> {
    return this.mapEntityToMappedModel(this.updateInternal(id, item, dispatchEvent));
  }

  public delete(id: string, dispatchEvent: boolean = true): MappedEntityModel<T> {
    const entity = this._get(id);
    const mapped = this.mapEntityToMappedModel(entity);
    this.deleteInternal(id, dispatchEvent);
    return mapped;
  }

  public dumpTable(): CreateModel<T>[] {
    const dump: CreateModel<T>[] = [];

    for (const key of keysOf(this.repository)) {
      const entity = this.repository[key];
      // @ts-ignore
      const createModel: CreateModel<T> = {};

      for (const entityKey of keysOf(entity)) {
        if (entityKey === 'internalGuid') {
          continue;
        }

        const entityValueOfKey = entity[entityKey];

        if (isOneOfConnection(entityValueOfKey) || isManyOfConnection(entityValueOfKey)) {
          continue;
        }
        // @ts-ignore
        createModel[entityKey] = entityValueOfKey;
      }
      dump.push(createModel);
    }
    return dump;
  }

  public restoreFromDump(dump: CreateModel<T>[]) {
    if (this.hasAny()) {
      throwError('Dump restore is only safe if tables are empty');
    }
    this.insertMultipleInternal(dump);
  }

  protected entityUpdated(entity: T) {
    // Implemented in child instances
  }

  protected entitiesUpdated(entities: T[]) {
    // Implemented in child instances
  }

  protected entityRemoved(id: string) {
    // Implemented in child instances
  }

  protected entityInserted(entity: T) {
    // Implemented in child instances
  }

  protected entitiesInserted(entities: T[]) {
    // Implemented in child instances
  }

  public dispose() {
    // Implemented in child instances
  }

  private _get(id: string): T | never {
    // @ts-ignore
    return this.repository[id] ?? throwError(`Entity not found with key ${id}`);
  }

  private insertInternal(item: CreateModel<T>, dispatchUpdate = true): T {
    const id = this.idOf(item);

    if (id in this.repository) {
      throwError(`Tried to re-insert database entry with ID ${id}`);
    }

    const entity = new this.classType(id);
    this.patchEntity(entity, item, 'insert');
    this.repository[id] = entity;

    if (dispatchUpdate) {
      this.database.eventBus.dispatchEvent('entitiesAdded', { classType: this.classType, items: { [entity.id]: entity } });
    }

    // Don't mix up the order of these 2 lines
    this.lazyConnectionMap.entityAdded(entity.id);
    this.entityInserted(entity);
    return entity;
  }

  private insertMultipleInternal(items: CreateModel<T>[], onConflict: 'error' | 'skip' | 'update' = 'update', dispatchEvent = true): T[] {
    const inserted: T[] = [];
    for (const item of items) {
      // @ts-ignore
      const id: TId = item['id'];

      if (this.repository[id] != null) {
        switch (onConflict) {
          case 'update':
            this.updateInternal(id as any, item as any);
            continue;
          case 'error':
            // Insert will break
            break;
          case 'skip':
            continue;
        }
      }

      inserted.push(this.insertInternal(item, false));
    }

    if (dispatchEvent) {
      this.database.eventBus.dispatchEvent('entitiesAdded', {
        classType: this.classType,
        items: inserted.reduce((obj, entity) => ({ ...obj, [entity.id]: entity }), {}),
      });
    }

    this.entitiesInserted(inserted);
    return inserted;
  }

  private updateInternal(id: string, item: PatchModel<T>, dispatchEvent = true): T {
    const entity = this._get(id);
    this.patchEntity(entity, item, 'update');

    if (dispatchEvent) {
      this.database.eventBus.dispatchEvent('entitiesUpdated', { classType: this.classType, items: { [entity.id]: entity } });
    }

    // Don't mix up the order of these 2 lines
    this.lazyConnectionMap.entityUpdated(id);
    this.entityUpdated(entity);

    return entity;
  }

  private deleteInternal(id: string, dispatchEvent: boolean = true): T {
    const entity = this._get(id);
    // @ts-ignore
    delete this.repository[id];

    if (dispatchEvent) {
      this.database.eventBus.dispatchEvent('entitiesRemoved', { classType: this.classType, items: { [entity.id]: entity } });
    }

    // Don't mix up the order of these 2 lines
    this.lazyConnectionMap.entityDeleted(id);
    this.entityRemoved(id);

    return entity;
  }

  // eslint-disable-next-line sonarjs/cognitive-complexity
  private mapEntityToMappedModel(entity: T): MappedEntityModel<T> {
    const ret: MappedEntityModel<T> = {} as any;

    for (const key of keysOf(entity)) {
      if ((key as keyof Entity) === 'internalGuid') {
        continue;
      }

      // @ts-ignore
      ret[key] = entity[key];
    }

    for (const metadata of this.joinMetadata) {
      // @ts-ignore
      ret[metadata.propertyKey] = this.lazyConnectionMap.getLazy(entity.id, metadata.propertyKey, metadata.joinedEntity, () => {
        const joinedEntities = this.database.getTable(metadata.joinedEntity).select((otherEntity) => {
          const otherEntityMetadataTargetValue = otherEntity[metadata.targetProperty] as string | string[];
          // @ts-ignore
          const selfMetadataTargetValue = entity[metadata.selfProperty] as string | string[];

          if (selfMetadataTargetValue instanceof Array) {
            return otherEntityMetadataTargetValue instanceof Array
              ? selfMetadataTargetValue.includesAny(otherEntityMetadataTargetValue)
              : selfMetadataTargetValue.includes(otherEntityMetadataTargetValue);
          } else {
            return otherEntityMetadataTargetValue instanceof Array
              ? otherEntityMetadataTargetValue.includes(selfMetadataTargetValue)
              : otherEntityMetadataTargetValue === selfMetadataTargetValue;
          }
        });

        if (metadata.cardinality === 'one') {
          if (joinedEntities.length > 1) {
            throwError('Multiple entity candidates fit the same join condition. Did you mean to use a many type connection?');
          }
          return joinedEntities.firstOrNull();
        } else {
          const manyOf: ReadOnlyManyOf<MappedEntityModel<Entity>> = {
            list: joinedEntities,
            record: joinedEntities.reduce((curr, next) => ({ ...curr, [next.id]: next }), {}),
          };
          return manyOf;
        }
      });
    }

    return ret;
  }
  private patchEntity(entity: T, item: CreateModel<T> | PatchModel<T>, operation: 'insert' | 'update') {
    const skippedKeys: (keyof Entity)[] = ['id', 'internalGuid', 'createTimestamp', 'lastUpdateTimestamp'];

    for (const key of keysOf(item)) {
      if (skippedKeys.includes(key as keyof Entity)) {
        continue;
      }

      // @ts-ignore
      entity[key] = item[key];
    }

    if (operation === 'update') {
      entity.lastUpdateTimestamp = Date.now();
    }
  }

  private signalUpdateIfNecessaryOnDbEvent(payload: BulkDbEvent<Entity>): void {
    if (this.lazyConnectionMap.hasDependencyOnClassType(payload.classType)) {
      this.entitiesUpdated(Object.values(this.repository));
    }
  }

  private idOf(entity: T): string;

  private idOf(entity: CreateModel<T>): string;

  private idOf(entity: T | CreateModel<T>): string {
    // @ts-ignore
    return entity['id'] as string;
  }
}
